import { useEffect, useRef, useState } from "react";

// Per-(product, node) localStorage cache for in-progress draft text. The
// orchestrator's /answers and /approvals endpoints already persist drafts
// server-side under LWW semantics, but they need an explicit submit. This
// hook defends the founder against an accidental tab close, network
// blip, or browser refresh BEFORE that submit. Cleared on successful
// submission via clearDraft().
//
// Key shape includes the product id so a draft for one founder doesn't
// leak across products on a shared browser. Not user-scoped because
// drafts already aren't sensitive (the founder typed them) and are
// namespaced by product id which is itself authenticated server-side.
const KEY = (productId: string, nodeId: string) => `pmt-draft-${productId}-${nodeId}`;

// `persistInitial: false` (0160: the spec drawer's withdrawn prefill) makes an
// uncached `initial` a prefill for this mount only: it is not stored until the
// value first differs from it (the founder's edit), and from then on the value
// is the founder's draft, stored as any other. A cached draft wins over
// `initial` either way.
export function useDraft(productId: string, nodeId: string, initial = "", opts: { persistInitial?: boolean } = {}) {
  const { persistInitial = true } = opts;
  const storageKey = KEY(productId, nodeId);
  const [value, setValue] = useState<string>(() => {
    try {
      const cached = localStorage.getItem(storageKey);
      return cached ?? initial;
    } catch {
      return initial;
    }
  });
  // Whether the value still is the initial this mount must not store.
  const holdingInitial = useRef(!persistInitial && value === initial);

  // Debounce localStorage writes so each keystroke doesn't hit storage.
  // 250ms is short enough to feel instant but long enough that a 30wpm
  // typist writes once per word, not once per character.
  const timeoutRef = useRef<number | null>(null);
  useEffect(() => {
    if (holdingInitial.current) {
      if (value === initial) return;
      holdingInitial.current = false;
    }
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => {
      try {
        if (value.length === 0) {
          localStorage.removeItem(storageKey);
        } else {
          localStorage.setItem(storageKey, value);
        }
      } catch {
        // Storage full or disabled — accept the loss; the server-side
        // draft (after submit) is still authoritative.
      }
    }, 250);
    return () => {
      if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
    };
  }, [value, storageKey]);

  const clear = () => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
    setValue("");
  };

  // commit writes the value to localStorage SYNCHRONOUSLY and updates
  // React state. This is the safety-net counterpart to the typing-time
  // debounced effect — used on save events where the panel is about
  // to unmount (effect cleanup would otherwise cancel the pending
  // 250ms timer, losing any keystroke that landed inside that window
  // — realistic for short answers like "yes" + Cmd+Enter).
  const commit = (newValue: string) => {
    setValue(newValue);
    try {
      if (newValue.length === 0) {
        localStorage.removeItem(storageKey);
      } else {
        localStorage.setItem(storageKey, newValue);
      }
    } catch {
      /* ignore — storage full/disabled */
    }
  };

  return [value, setValue, clear, commit] as const;
}
